import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cardImageAt } from "@/lib/card-image";
import {
  type HolidayNavItem,
  type NavCategory,
  type NavChild,
  useCardNav,
  useHolidayNav,
  usePrimaryNav,
} from "@/site/components/category-nav-data";

/**
 * Typed link to a catalog category by full_path (old `fullCardCategorySlug`).
 * An empty path is the "All Cards" root → `/cards`; everything else routes into
 * the `/cards/$` browser with the path as the splat.
 */
export function CategoryLink({
  path,
  className,
  children,
  ...rest
}: {
  path: string;
  className?: string;
  children: React.ReactNode;
} & React.ComponentProps<"a">) {
  return path ? (
    <Link to="/cards/$" params={{ _splat: path }} className={className} {...rest}>
      {children}
    </Link>
  ) : (
    <Link to="/cards" className={className} {...rest}>
      {children}
    </Link>
  );
}

// The 60px category bar that sits under the main header on card-browsing
// surfaces (old `components/cards/card-navigation`, gated by `withCardNav`).
// Occasions open a mega-menu of their subcategories. "All Cards" is the old
// app's "MORE" overflow tab: the LAST tab before search, plain (no icon, no
// divider), whose flyout lists the published top-level categories not curated
// into the bar. Data is live from `/api/catalog/card-nav` (admin-curated +
// ordered), via `useCardNav`.

/**
 * Old tab tooltip was the literal `${shortCategoryName} Cards` template, which
 * rendered "All Cards Cards" once the category was renamed (the deployed app's
 * live tooltip bug). Guard it so a label already ending in "Cards" is left as-is.
 */
const tabTitle = (label: string) => (/\bcards$/i.test(label) ? label : `${label} Cards`);

/** Compact "Search all cards" pill (old `.card-category-search`, max 220px). */
function NavSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  return (
    <form
      className="ml-5 flex h-[40px] w-[220px] max-w-full items-center max-[1279px]:ml-3 max-[1279px]:w-[190px] rounded-[30px] border border-black/[0.23] bg-background px-4 transition-colors hover:border-black/40 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50"
      onSubmit={(e) => {
        e.preventDefault();
        navigate({ to: "/home", search: value.trim() ? { q: value.trim() } : {} });
      }}
    >
      <Search className="shrink-0 text-foreground" size={18} />
      <input
        type="text"
        placeholder="Search all cards"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="min-w-0 flex-1 bg-transparent px-3 text-base text-foreground outline-none placeholder:text-grey"
      />
    </form>
  );
}

/** Placeholder bar widths shown while the live card-nav query loads. */
const NAV_SKELETON = [64, 56, 92, 116, 84];

/**
 * The mega-menu for a single occasion: a grid of its published subcategories,
 * read column-major (down each column, then across) like the deployed old nav
 * and this file's own DirectoryMenu columns — not flex-wrap's row-major order.
 */
/**
 * The flyout's image rail — old `.card-category-images`: right-aligned 150×194 tiles
 * beside the subcategory list (padding 20px 0, 5px per tile, 0.7s fade-in), wrapping
 * two per row at the panel's right edge. Legacy filled it from `account_menu_image`
 * (admin promo tiles with their own link targets), which never migrated; PRD 01 §10
 * maps those to the category's own poster/primary still, so the rail now shows that
 * one image, linking to the category (BDAY-37). Nothing renders until the category
 * has an image, so a bare tab keeps exactly the layout it has today.
 */
function NavImageRail({ category, onClose }: { category: NavCategory; onClose: () => void }) {
  if (!category.imageUrl) return null;
  return (
    <div className="flex flex-1 flex-wrap justify-end py-5">
      <div className="shrink-0 p-[5px]">
        <Link
          to="/cards/$"
          params={{ _splat: category.path }}
          onClick={onClose}
          className="block outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <img
            src={cardImageAt(category.imageUrl, { width: 300 })}
            alt={category.label}
            width={150}
            height={194}
            // Category stills are square (600×600) where the old promo tiles were
            // portrait 150×194 — cover-crop into the old box rather than letterbox it.
            className="h-[194px] w-[150px] object-cover animate-in fade-in fill-mode-forwards delay-100 duration-700 ease-[cubic-bezier(0.23,1,0.32,1)]"
          />
        </Link>
      </div>
    </div>
  );
}

function OccasionMenu({ category, onClose }: { category: NavCategory; onClose: () => void }) {
  // Old `.card-category-children` is a fixed 710px block — three 200px columns —
  // with the image rail taking the rest of the 1100px panel (`.card-navigation-menu-wrapper`:
  // flex, space-between, wrap). Five columns only ever fit because the rail was missing.
  const rows = Math.max(1, Math.ceil(category.children.length / 3));
  return (
    <div className="mx-auto flex w-[1100px] max-w-full flex-wrap justify-between">
      <div
        className="grid w-[710px] max-w-full grid-flow-col auto-cols-[200px] content-start justify-start py-1.5"
        style={{ gridTemplateRows: `repeat(${rows}, auto)` }}
      >
        {category.children.map((child) => (
          <Link
            key={child.label}
            to="/cards/$"
            params={{ _splat: child.path }}
            onClick={onClose}
            className="flex cursor-pointer items-center justify-between px-4 py-2 text-foreground transition-colors outline-none hover:bg-hover-grey hover:text-primary-dark hover:no-underline focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset"
          >
            <span className="flex-1 overflow-hidden text-[0.9rem] font-semibold text-ellipsis whitespace-nowrap">
              {child.label}
            </span>
          </Link>
        ))}
      </div>
      <NavImageRail category={category} onClose={onClose} />
    </div>
  );
}

/**
 * The "All Cards" flyout — the old app's "MORE" overflow tab (BDAY-149 #3):
 * lists the published top-level categories NOT curated into the tab bar
 * (`is_in_card_nav = false`) — Get Well, Thank You, Just Because, etc. In the
 * deployed old app this list came back empty, so the flyout rendered blank
 * (Trello #2345); populating it from the primary-nav feed is the fix. Laid out
 * like the old `.card-navigation-menu` flyout: column-major 200px columns, and
 * balanced across the panel the way the old populated flyout read (~5 columns)
 * — a height-capped flex column would leave a short list as one tall strip.
 * Per-category promo images are deferred to BDAY-37. The column-per-occasion map
 * survives only as a fallback while the list loads or if nothing is left over.
 */
function DirectoryMenu({
  occasions,
  more,
  moreLoading,
  onClose,
}: {
  occasions: NavCategory[];
  more: NavChild[];
  moreLoading: boolean;
  onClose: () => void;
}) {
  const rows = Math.max(1, Math.ceil(more.length / 5));
  return (
    <div className="mx-auto w-[1100px] max-w-full px-5 py-1.5">
      {more.length > 0 ? (
        <div
          className="grid grid-flow-col auto-cols-[200px] content-start justify-start"
          style={{ gridTemplateRows: `repeat(${rows}, auto)` }}
        >
          {more.map((c) => (
            <CategoryLink
              key={c.path}
              path={c.path}
              onClick={onClose}
              className="flex cursor-pointer items-center px-4 py-2 text-foreground transition-colors outline-none hover:bg-hover-grey hover:text-primary-dark hover:no-underline focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset"
            >
              <span className="flex-1 overflow-hidden text-[0.9rem] font-semibold text-ellipsis whitespace-nowrap">
                {c.label}
              </span>
            </CategoryLink>
          ))}
        </div>
      ) : moreLoading ? (
        // First open: the primary-nav list is still on the wire.
        <div className="flex flex-wrap gap-x-6 gap-y-3 py-5">
          {NAV_SKELETON.map((w, i) => (
            <span
              key={`dsk-${w}-${i}`}
              aria-hidden
              className="h-4 animate-pulse rounded bg-black/10"
              style={{ width: w }}
            />
          ))}
        </div>
      ) : (
        renderOccasionDirectory(occasions, onClose)
      )}
    </div>
  );
}

/**
 * Fallback directory — a column per tab-bar occasion with its top subcategories.
 * Only shown when every published top-level category is already in the bar (so
 * the overflow list above would be empty).
 */
function renderOccasionDirectory(occasions: NavCategory[], onClose: () => void) {
  return (
    <div className="grid grid-cols-2 gap-x-6 py-5 sm:grid-cols-3 lg:grid-cols-5">
      {occasions.map((c) => (
        <div key={c.label} style={{ "--accent": c.accent } as React.CSSProperties}>
          <CategoryLink
            path={c.path}
            onClick={onClose}
            className="inline-block rounded-sm text-[0.9rem] font-bold text-[var(--accent)] uppercase transition-colors outline-none hover:no-underline focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            {c.label}
          </CategoryLink>
          <ul className="mt-2 flex flex-col">
            {c.children.slice(0, 5).map((child) => (
              <li key={child.label}>
                <Link
                  to="/cards/$"
                  params={{ _splat: child.path }}
                  onClick={onClose}
                  className="-mx-2 block overflow-hidden rounded-sm px-2 py-1.5 text-[0.9rem] font-semibold text-foreground text-ellipsis whitespace-nowrap transition-colors outline-none hover:bg-hover-grey hover:text-primary-dark focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset"
                >
                  {child.label}
                </Link>
              </li>
            ))}
            {c.children.length > 0 && (
              <li>
                <CategoryLink
                  path={c.path}
                  onClick={onClose}
                  className="mt-1 inline-block rounded-sm text-[0.9rem] font-semibold text-primary transition-colors outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50"
                >
                  View all →
                </CategoryLink>
              </li>
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** English ordinal suffix: 1 → "1st", 22 → "22nd", 25 → "25th". */
function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

/** Format an ISO date as the old Holiday tab's short, year-less "Dec 25th". */
function formatHolidayDate(iso: string | null): string | null {
  if (!iso) return null;
  const [, m, d] = iso.split("-").map(Number);
  if (!m || !d) return null;
  return `${MONTHS[m - 1]} ${ordinal(d)}`;
}

/**
 * The Holiday tab flyout, mirroring the old app's special Holiday treatment: a
 * single column-major list (old `.flex-column`, ~450px tall, fills down each
 * 200px column then wraps across) of HOLIDAYS — each with its next date — that
 * have a card category, soonest-first. Like legacy (api.py nav: the holiday tab's
 * children are replaced by `get_cached_holiday_categories`), the tab's static
 * subcategories are NOT listed here (2026-10-01, reverting BDAY-149 #1's appended
 * undated extras); they stay reachable from the Holidays category page.
 */
function HolidayMenu({
  holidays,
  category,
  onClose,
}: {
  holidays: HolidayNavItem[];
  category: NavCategory;
  onClose: () => void;
}) {
  return (
    <div className="mx-auto flex w-[1100px] max-w-full flex-wrap justify-between">
      <ul className="mt-[6px] flex max-h-[450px] min-w-0 flex-1 flex-col flex-wrap content-start">
        {holidays.map((h) => {
          const date = formatHolidayDate(h.next_on);
          return (
            <li key={h.occasion_id} className="w-[200px]">
              <Link
                to="/cards/$"
                params={{ _splat: h.full_path }}
                onClick={onClose}
                className="flex items-center justify-between px-4 py-2 text-foreground transition-colors outline-none hover:bg-hover-grey hover:text-primary-dark hover:no-underline focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset"
              >
                <span className="flex-1 overflow-hidden text-[0.9rem] font-semibold text-ellipsis whitespace-nowrap">
                  {h.occasion_name}
                </span>
                {date && (
                  <span className="shrink-0 pl-[5px] font-mono text-[10px] text-grey">{date}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
      <NavImageRail category={category} onClose={onClose} />
    </div>
  );
}

type OpenMenu = number | "all" | null;

/** Horizontal category bar + hover flyouts, shown under the header on browse surfaces. */
export function CategoryNav() {
  // Live, admin-curated categories (is_in_card_nav / primary-nav fallback, ordered by
  // `position`). "All Cards" stays a synthetic leading anchor — not a real category.
  const { data, isLoading } = useCardNav();
  const occasions = data ?? [];

  // Upcoming holidays for the Holiday tab. Each holiday attaches to whichever nav
  // tab owns its category path (e.g. `holidays/christmas` → the `holidays` tab), so
  // no tab is hard-coded; a tab with holidays shows them instead of subcategories.
  const { data: holidayData } = useHolidayNav();
  const holidaysForTab = (path: string) =>
    (holidayData ?? []).filter((h) => h.full_path === path || h.full_path.startsWith(`${path}/`));

  // Single open menu (an occasion index or the "all" directory) + a close timer
  // so moving between a trigger and its flyout doesn't flicker.
  const [open, setOpen] = useState<OpenMenu>(null);

  // The "All Cards" overflow directory: every primary-nav top-level category not
  // already in the bar. Held until the flyout first opens (hover fires it), then
  // cached — same query key as /home's pill row.
  const { data: primaryNav, isLoading: primaryNavLoading } = usePrimaryNav({
    enabled: open === "all",
  });
  const barPaths = new Set(occasions.map((c) => c.path));
  const overflow = (primaryNav ?? []).filter((c) => !barPaths.has(c.path));

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();

  const openMenu = (id: OpenMenu) => {
    if (timer.current) clearTimeout(timer.current);
    if (id === "all") return setOpen("all");
    if (typeof id === "number") {
      const c = occasions[id];
      setOpen(c && c.children.length > 0 ? id : null);
    }
  };
  const scheduleClose = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(null), 120);
  };
  const close = () => setOpen(null);
  // A pending close would fire setOpen on an unmounted nav (route change straight
  // off a hover) — drop it with the component.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  // Active state: "All Cards" owns the /cards root; occasions own their subtree.
  const allActive = pathname === "/cards";
  const isOccActive = (p: string) =>
    pathname === `/cards/${p}` || pathname.startsWith(`/cards/${p}/`);

  const activeOccasion = typeof open === "number" ? occasions[open] : null;

  return (
    <nav
      aria-label="Card categories"
      onMouseLeave={scheduleClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(null);
      }}
      onBlur={(e) => {
        // Close when keyboard focus leaves the nav entirely.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(null);
      }}
      className="relative z-30 hidden h-[60px] bg-background card-browser:block"
    >
      {/* Tight widths (the bar appears at 1070px): labels never wrap ("GROUP / CARDS"
          used to stack); below 1280 the tabs, type and search tighten so the whole
          row fits on one line. */}
      <ul className="flex h-full items-center justify-center gap-1">
        {isLoading &&
          NAV_SKELETON.map((w, i) => (
            <li key={`sk-${w}-${i}`} aria-hidden className="flex h-[60px] items-center px-4">
              <span className="h-4 animate-pulse rounded bg-black/10" style={{ width: w }} />
            </li>
          ))}

        {occasions.map((c, i) => (
          <li
            key={c.label}
            className="group relative"
            style={{ "--accent": c.accent } as React.CSSProperties}
            onMouseEnter={() => openMenu(i)}
            onFocus={() => openMenu(i)}
          >
            <CategoryLink
              path={c.path}
              title={tabTitle(c.label)}
              aria-haspopup
              aria-expanded={open === i}
              className={`relative flex h-[60px] cursor-pointer items-center px-4 text-base font-bold whitespace-nowrap uppercase max-[1279px]:px-3 max-[1279px]:text-[15px] transition-colors outline-none group-hover:text-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset ${
                isOccActive(c.path) ? "text-[var(--accent)]" : "text-foreground"
              }`}
            >
              <span
                aria-hidden
                className={`pointer-events-none absolute top-0 left-0 h-1 w-full transition-colors group-hover:bg-[var(--accent)] ${
                  isOccActive(c.path) ? "bg-[var(--accent)]" : "bg-transparent"
                }`}
              />
              {c.label}
            </CategoryLink>
          </li>
        ))}

        {/* All Cards — the old "MORE" overflow tab: last tab before search, plain
            (no icon, no divider), grey accent, opens the directory flyout. */}
        <li
          className="group relative"
          style={{ "--accent": "#666666" } as React.CSSProperties}
          onMouseEnter={() => openMenu("all")}
          onFocus={() => openMenu("all")}
        >
          <Link
            to="/cards"
            title={tabTitle("All Cards")}
            aria-haspopup
            aria-expanded={open === "all"}
            className={`relative flex h-[60px] cursor-pointer items-center px-4 text-base font-bold whitespace-nowrap uppercase max-[1279px]:px-3 max-[1279px]:text-[15px] transition-colors outline-none group-hover:text-[var(--accent)] hover:no-underline focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset ${
              allActive || open === "all" ? "text-[var(--accent)]" : "text-foreground"
            }`}
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute top-0 left-0 h-1 w-full transition-colors group-hover:bg-[var(--accent)] ${
                allActive || open === "all" ? "bg-[var(--accent)]" : "bg-transparent"
              }`}
            />
            All Cards
          </Link>
        </li>

        <li className="flex items-center" onMouseEnter={scheduleClose} onFocus={close}>
          <NavSearch />
        </li>
      </ul>

      {/* Flyout: occasion mega-menu or the "All Cards" directory. The outer panel
          stays mounted while switching (so it doesn't re-slide); the inner content is
          keyed so it crossfades between menus instead of hard-swapping. */}
      {open !== null && (
        <div
          className="absolute top-full left-0 z-[1000] w-screen animate-in bg-white pb-5 shadow-[0_8px_10px_0_rgba(0,0,0,0.2)] duration-150 fade-in-0 slide-in-from-top-1"
          onMouseEnter={() => openMenu(open)}
          onMouseLeave={scheduleClose}
        >
          <div key={String(open)} className="animate-in duration-150 fade-in-0">
            {open === "all" ? (
              <DirectoryMenu
                occasions={occasions}
                more={overflow}
                moreLoading={primaryNavLoading}
                onClose={close}
              />
            ) : activeOccasion ? (
              holidaysForTab(activeOccasion.path).length > 0 ? (
                <HolidayMenu
                  holidays={holidaysForTab(activeOccasion.path)}
                  category={activeOccasion}
                  onClose={close}
                />
              ) : (
                <OccasionMenu category={activeOccasion} onClose={close} />
              )
            ) : null}
          </div>
        </div>
      )}
    </nav>
  );
}
